import React from 'react';
import { Card, Tooltip } from 'antd';
import {
  ClockCircleOutlined,
  ShoppingCartOutlined,
  CheckCircleOutlined,
  ArrowRightOutlined,
  QuestionCircleOutlined,
} from '@ant-design/icons';

const COLORS = {
  success: '#52C41A',
  warning: '#FAAD14',
  danger: '#FF4D4F',
  brand: '#00A651',
  blue: '#1890ff',
};

/**
 * Module 2: Fulfillment Pipeline
 * Horizontal step-based pipeline showing order flow with counts and avg times
 */
const FulfillmentPipeline = ({ data }) => {
  if (!data) return null;

  const nodes = [
    {
      label: 'Waiting',
      icon: <ClockCircleOutlined />,
      count: data.waitingOrders,
      time: data.avgWaitTime,
      timeLabel: 'Avg Wait',
      color: COLORS.warning,
      bg: '#fffbe6',
      timeWarning: data.avgWaitTime > 10,
    },
    {
      label: 'Pick & Pack',
      icon: <ShoppingCartOutlined />,
      count: data.pickingOrders,
      time: data.avgPickingTime,
      timeLabel: 'Avg Pick',
      color: COLORS.blue,
      bg: '#e6f7ff',
      subMetrics: [
        { label: 'Avg Pick Time /SKU', value: `${data.skuPickingTime} min` },
        { label: 'Avg Pick & Pack', value: `${data.avgPickAndPackTime} min` },
      ],
    },
    {
      label: 'Ready to Delivery',
      icon: <CheckCircleOutlined />,
      count: data.completedOrders,
      time: null,
      timeLabel: null,
      color: COLORS.success,
      bg: '#f6ffed',
      subMetrics: [
        { label: 'Avg waiting for delivery', value: `${data.avgWaitingForDelivery} min` },
      ],
    },
  ];

  return (
    <Card
      title="Fulfillment Pipeline"
      extra={
        <div style={{ display: 'flex', gap: 24, fontSize: 13, alignItems: 'center' }}>
          <span>
            End-to-End: <b style={{ color: COLORS.danger, fontSize: 16 }}>{data.totalProcessingTime} min</b>
            <Tooltip 
              title={
                <div>
                  <div style={{ fontWeight: 'bold', marginBottom: 4 }}>End-to-End Time Calculation:</div>
                  <div>Avg Wait ({data.avgWaitTime} min)</div>
                  <div>+ Avg Pick & Pack ({data.avgPickAndPackTime} min)</div>
                  <div>+ Avg waiting for delivery ({data.avgWaitingForDelivery} min)</div>
                  <div style={{ borderTop: '1px solid rgba(255,255,255,0.3)', marginTop: 4, paddingTop: 4 }}>
                    = {data.totalProcessingTime} min
                  </div>
                </div>
              }
            >
              <QuestionCircleOutlined style={{ marginLeft: 4, color: '#999', cursor: 'pointer' }} />
            </Tooltip>
          </span>
        </div>
      }
      styles={{ body: { padding: '16px 24px' } }}
    >
      <div style={{ display: 'flex', alignItems: 'stretch', gap: 0 }}>
        {nodes.map((node, index) => (
          <React.Fragment key={node.label}>
            {/* Pipeline Node */}
            <div
              style={{
                flex: 1,
                background: node.bg,
                borderRadius: 8,
                padding: '16px 12px',
                border: `1px solid ${node.color}22`,
                position: 'relative',
                minWidth: 0,
              }}
            >
              {/* Header with icon */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 12 }}>
                <span style={{ fontSize: 18, color: node.color }}>{node.icon}</span>
                <span style={{ fontSize: 14, fontWeight: 600, color: '#333' }}>{node.label}</span>
              </div>

              {/* Count - big number on top */}
              <div style={{ fontSize: 36, fontWeight: 'bold', color: node.color, lineHeight: 1, marginBottom: 8 }}>
                {typeof node.count === 'number' ? node.count.toLocaleString() : node.count}
              </div>
              <div style={{ fontSize: 12, color: '#999', marginBottom: 8 }}>orders</div>

              {/* Average time below */}
              {node.time !== null && (
                <div
                  style={{
                    fontSize: 14,
                    color: node.timeWarning ? COLORS.danger : '#666',
                    fontWeight: node.timeWarning ? 'bold' : 'normal',
                    marginBottom: 4,
                  }}
                >
                  {node.timeLabel}: <b>{node.time} min</b>
                </div>
              )}

              {/* Sub-metrics if any */}
              {node.subMetrics && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 2, marginTop: 4 }}>
                  {node.subMetrics.map((m) => (
                    <div key={m.label} style={{ fontSize: 12, color: '#888' }}>
                      {m.label}: <b>{m.value}</b>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Arrow between nodes */}
            {index < nodes.length - 1 && (
              <div style={{ display: 'flex', alignItems: 'center', padding: '0 8px', color: '#d9d9d9' }}>
                <ArrowRightOutlined style={{ fontSize: 20 }} />
              </div>
            )}
          </React.Fragment>
        ))}
      </div>
    </Card>
  );
};

export default FulfillmentPipeline;
